/**
 * Iter144 — Super-Admin Zefix-PublicREST Konfiguration.
 *
 * Wenn Zugangsdaten (vom BJ vergeben) gesetzt und getestet sind, wechselt
 * das Backend automatisch vom hidden Fallback auf den offiziellen Endpoint
 * — dann werden auch Straße & PLZ aus dem Handelsregister geliefert.
 */
import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { ZefixExportButton } from "@/components/ZefixExportButton";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Save, TestTube2, ExternalLink, Info, CheckCircle2, AlertCircle } from "lucide-react";

export default function ZefixSystemConfigCard() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState({ username: "", password: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/admin/zefix-config");
      setCfg(r.data);
      if (r.data.configured) {
        setForm({ username: r.data.username || "", password: "" });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zefix-Config nicht ladbar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.username.trim()) {
      toast.error("Benutzername erforderlich");
      return;
    }
    setSaving(true);
    try {
      const payload = { username: form.username.trim() };
      if (form.password) payload.password = form.password;
      await apiClient.put("/admin/zefix-config", payload);
      toast.success("Zugangsdaten gespeichert");
      setForm({ ...form, password: "" });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await apiClient.post("/admin/zefix-config/test");
      if (r.data.ok) {
        toast.success(`✓ ${r.data.detail}`);
      } else {
        toast.error(r.data.detail);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test fehlgeschlagen");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6 flex items-center justify-center text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade …
      </Card>
    );
  }

  return (
    <Card className="p-5" data-testid="zefix-system-config-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-slate-900">Zefix — Offizieller Endpoint</span>
            {cfg?.use_official ? (
              <Badge className="bg-emerald-100 text-emerald-800">Aktiv (mit Adressen)</Badge>
            ) : cfg?.configured ? (
              <Badge className="bg-amber-100 text-amber-800">Konfiguriert, nicht getestet</Badge>
            ) : (
              <Badge className="bg-slate-100 text-slate-700">Fallback (nur Ort)</Badge>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Basic-Auth Zugangsdaten vom Bundesamt für Justiz (BJ). Aktiv →
            Zefix-Suche liefert Straße + PLZ.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-3 flex items-start gap-2 mb-4">
        <Info className="h-4 w-4 text-indigo-600 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-indigo-900 leading-relaxed">
          Zugangsdaten anfordern via
          <a
            href="mailto:zefix@bj.admin.ch"
            className="text-indigo-700 underline mx-1 font-medium"
          >zefix@bj.admin.ch</a>
          → Formular ausfüllen → Basic-Auth-Zugang wird per Post/Mail zugestellt.
          Ohne Zugangsdaten läuft die Suche über den hidden Fallback (keine Adress-Daten).
          <div className="mt-1.5">
            <a
              href="https://www.zefix.admin.ch/ZefixPublicREST/swagger-ui/index.html"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-indigo-700 underline"
            >
              Swagger-Doku öffnen <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="zfx-user">Benutzername</Label>
          <Input
            id="zfx-user"
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            placeholder="z.B. info@firma.ch"
            data-testid="zefix-username-input"
          />
        </div>
        <div>
          <Label htmlFor="zfx-pass">
            Passwort
            {cfg?.configured && (
              <span className="text-xs text-slate-500 ml-2">
                (leer lassen → aktuelles behalten: {cfg?.password_masked})
              </span>
            )}
          </Label>
          <Input
            id="zfx-pass"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder={cfg?.configured ? "••••••••" : "vom BJ vergebenes Passwort"}
            data-testid="zefix-password-input"
          />
        </div>
      </div>

      {cfg?.last_test_at && (
        <div className={`mt-3 rounded-lg border p-2.5 text-xs flex items-start gap-2 ${
          cfg.last_test_ok
            ? "border-emerald-200 bg-emerald-50 text-emerald-900"
            : "border-rose-200 bg-rose-50 text-rose-900"
        }`}>
          {cfg.last_test_ok
            ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />
            : <AlertCircle className="h-4 w-4 text-rose-600 mt-0.5" />
          }
          <div className="flex-1">
            <div className="font-medium">Letzter Test: {cfg.last_test_detail}</div>
            <div className="text-[10px] opacity-70">{new Date(cfg.last_test_at).toLocaleString("de-CH")}</div>
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        <Button
          onClick={save}
          disabled={saving || !form.username.trim()}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="zefix-save-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
          Speichern
        </Button>
        <Button
          variant="outline"
          onClick={test}
          disabled={testing || !cfg?.configured}
          data-testid="zefix-test-btn"
        >
          {testing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <TestTube2 className="h-4 w-4 mr-1.5" />}
          Test-Suche (Migros)
        </Button>
        <ZefixExportButton
          url="/admin/zefix-config/export"
          label="Zugangsdaten exportieren"
          requirePassword
          size="default"
          disabled={!cfg?.configured}
          testId="zefix-credentials-export-btn"
        />
      </div>
    </Card>
  );
}
